import { useState } from "react";
import { arrayMove } from "@tellescope/utils";
import { useStripeAccounts } from "./useStripeAccounts";
import { useShopStripeProducts } from "./useShopStripeProducts";
import { useShopStripePrices } from "./useShopStripePrices";
import { useResolveTellescopeProducts } from "./useResolveTellescopeProducts";
import { buildSelectedShopVariant, buildSelectedShopProductFields } from "./buildSelectedShopEntry";
import type { SelectedShopEntry } from "./types";
import type { StripeShopProduct, StripeShopPrice } from "./stripeShopTypes";
import type { ShopProduct } from "../enduser/types";
import type { VisibilityCondition } from "@tellescope/visibility-schema";

export interface UseShopBlockEditorLogicOptions {
  initialSelection: SelectedShopEntry[];
  initialStripeKey?: string | null;
}

export const useShopBlockEditorLogic = ({
  initialSelection,
  initialStripeKey = null,
}: UseShopBlockEditorLogicOptions) => {
  const [stripeKey, setStripeKeyState] = useState<string | null>(initialStripeKey);
  const [selection, setSelection] = useState<SelectedShopEntry[]>(initialSelection);
  const [drillDownProductId, setDrillDownProductId] = useState<string | null>(null);

  // Every product in a block's selection must come from the same Stripe account - mixing accounts
  // risks cross-account customer ID confusion at checkout (one customer record per account).
  // Switching accounts mid-edit clears the current selection rather than leaving stale entries
  // from the old account mixed in with new ones.
  const setStripeKey = (nextStripeKey: string) => {
    setStripeKeyState(nextStripeKey);
    setSelection([]);
  };

  const { accounts, isLoading: accountsLoading, error: accountsError } = useStripeAccounts();
  const productsQuery = useShopStripeProducts(stripeKey);
  const pricesQuery = useShopStripePrices(stripeKey, drillDownProductId);

  const selectedStripePriceIds = selection.flatMap((entry) => entry.variants.map((v) => v.stripePriceId));
  const {
    productByStripePriceId,
    isLoading: resolutionLoading,
    hasChecked: resolutionChecked,
    hasCheckedPriceId,
  } = useResolveTellescopeProducts(selectedStripePriceIds);

  const isPriceSelected = (stripePriceId: string) =>
    selection.some((entry) => entry.variants.some((v) => v.stripePriceId === stripePriceId));

  const togglePrice = (product: StripeShopProduct, price: StripeShopPrice) => {
    setSelection((prev) => {
      const existing = prev.find((entry) => entry.stripeProductId === product.id);

      if (!existing) {
        const newEntry: SelectedShopEntry = {
          ...buildSelectedShopProductFields(product),
          variants: [buildSelectedShopVariant(price)],
          recommended: false,
        };
        return [...prev, newEntry];
      }

      const hasVariant = existing.variants.some((v) => v.stripePriceId === price.id);
      const nextVariants = hasVariant
        ? existing.variants.filter((v) => v.stripePriceId !== price.id)
        : [...existing.variants, buildSelectedShopVariant(price)];

      if (nextVariants.length === 0) {
        return prev.filter((entry) => entry.stripeProductId !== product.id);
      }

      return prev.map((entry) => (entry.stripeProductId === product.id ? { ...entry, variants: nextVariants } : entry));
    });
  };

  const removeSelection = (stripeProductId: string) => {
    setSelection((prev) => prev.filter((entry) => entry.stripeProductId !== stripeProductId));
  };

  const reorderSelection = (fromIndex: number, toIndex: number) => {
    setSelection((prev) => arrayMove(prev, fromIndex, toIndex));
  };

  // v1 ships without form-gated products, so FormAssociationPicker (the only UI that called this)
  // was removed from ProductListItem/ShopBlockEditor. Kept, uncalled, so the forms fast follow
  // can re-wire a picker back to this setter without rebuilding the selection-mutation logic.
  const setEntryFormId = (stripeProductId: string, formId: string | undefined) => {
    setSelection((prev) =>
      prev.map((entry) => (entry.stripeProductId === stripeProductId ? { ...entry, formId } : entry))
    );
  };

  const setEntryBadge = (stripeProductId: string, badge: string | undefined) => {
    setSelection((prev) =>
      prev.map((entry) => (entry.stripeProductId === stripeProductId ? { ...entry, badge } : entry))
    );
  };

  const setEntryRecommended = (stripeProductId: string, recommended: boolean) => {
    setSelection((prev) =>
      prev.map((entry) => (entry.stripeProductId === stripeProductId ? { ...entry, recommended } : entry))
    );
  };

  const setEntryCategory = (stripeProductId: string, category: string | undefined) => {
    setSelection((prev) =>
      prev.map((entry) => (entry.stripeProductId === stripeProductId ? { ...entry, category } : entry))
    );
  };

  const setEntryVisibility = (stripeProductId: string, condition: VisibilityCondition | undefined) => {
    setSelection((prev) =>
      prev.map((entry) =>
        entry.stripeProductId === stripeProductId
          ? { ...entry, visibility: condition ? { condition } : undefined }
          : entry
      )
    );
  };

  const unresolvedSelections = resolutionChecked
    ? selection.filter((entry) => entry.variants.some((v) => !productByStripePriceId.has(v.stripePriceId)))
    : [];

  const canSave = resolutionChecked && !resolutionLoading && unresolvedSelections.length === 0 && selection.length > 0;

  /** The shop block's snapshotted props.products entries — display data was already captured on each entry at selection time. */
  const buildSnapshot = (): ShopProduct[] | null => {
    if (!canSave) return null;
    return selection.map((entry) => ({
      stripeProductId: entry.stripeProductId,
      title: entry.title,
      description: entry.description,
      image: entry.image,
      formId: entry.formId,
      variants: entry.variants,
      badge: entry.badge,
      recommended: entry.recommended,
      category: entry.category,
      visibility: entry.visibility,
    }));
  };

  return {
    stripeKey,
    setStripeKey,
    accounts,
    accountsLoading,
    accountsError,
    productsQuery,
    drillDownProductId,
    setDrillDownProductId,
    pricesQuery,
    selection,
    isPriceSelected,
    togglePrice,
    removeSelection,
    reorderSelection,
    setEntryFormId,
    setEntryBadge,
    setEntryRecommended,
    setEntryCategory,
    setEntryVisibility,
    productByStripePriceId,
    unresolvedSelections,
    resolutionLoading,
    resolutionChecked,
    hasCheckedPriceId,
    canSave,
    buildSnapshot,
  };
};
